"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const pillars = [{
  id: "medical-centers",
  title: "Medical Network",
  description: "A growing network of our clinics, medical institutions and strategic partners forming the real-world foundation of the AizaHCare ecosystem.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581901/img/medical_centers_real_world.png"
}, {
  id: "patient-data",
  title: "Real Patient Data",
  description: "Patient data sourced through healthcare partnerships, existing datasets and future deployments, continuously improving our AI models and healthcare outcomes.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581901/img/patient_data_abstract_viz.png"
}, {
  id: "ai-solutions",
  title: "AI Healthcare Solutions",
  description: "AI-native healthcare solutions powered by our Learning Care Loop, focusing on diabetes management, elder care, women's health and mental health.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581901/img/ai_solutions_software_ui.png"
}, {
  id: "maida-compute",
  title: "MAIDA Infrastructure",
  description: "MAIDA provides sustainable cloud and sovereign AI infrastructure through green data centers across Africa, starting with a 50MW AI data center in Morocco.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581901/img/maida_compute_server_blade.png"
}];
export default function MedicalEcosystemFoundation() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="medical-ecosystem-foundation" className="bg-white text-foreground py-24 px-4 md:px-8 lg:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerReveal.ref} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-sans font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight mb-6 text-foreground">
            Real World Foundation
          </h2>
          <p className="font-sans font-light text-lg md:text-xl text-muted-foreground leading-relaxed">Aizalea is not just a concept. It is the utility token powering the AizaHcare ecosystem, an AI-native learning healthcare infrastructure built on our medical network, patient data, AI healthcare solutions, and sovereign compute and cloud data centers.</p>
        </div>

        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 delay-200 ease-out ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {pillars.map((pillar, i) => <div data-index={i} key={pillar.id} className="h-full">
              <Card className="group h-full flex flex-col overflow-hidden border-border/40 bg-card/40 backdrop-blur-md transition-all duration-500 hover:-translate-y-2 hover:bg-primary/10 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5">
                <div className="aspect-[4/3] w-full overflow-hidden bg-muted/20">
                  <img src={pillar.image} alt={pillar.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100" loading="lazy" />
                </div>
                <CardHeader className="flex-1 flex flex-col justify-start p-6">
                  <CardTitle className="font-sans font-bold text-xl tracking-tight text-foreground mb-3">
                    {pillar.title}
                  </CardTitle>
                  <CardDescription className="font-sans font-light text-base leading-relaxed text-muted-foreground">
                    {pillar.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}